import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { PlanAccessManager } from '@/components/common/PlanAccess'
import {
  useAdminPlanShares,
  useCreateAdminPlanShare,
  useDeleteAdminPlanShare,
  useUpdateAdminPlanShareFreeze,
} from '@/hooks/useAdminPlans'
import type { ApiPlanSummary } from '@/types'

interface ManageSharesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  plan: ApiPlanSummary | null
}

export function ManageSharesDialog({
  open,
  onOpenChange,
  plan,
}: ManageSharesDialogProps) {
  const planId = plan?.id

  const { data: sharesData, isLoading: isLoadingShares } =
    useAdminPlanShares(planId)
  const createShareMutation = useCreateAdminPlanShare(planId)
  const deleteShareMutation = useDeleteAdminPlanShare(planId)
  const freezeMutation = useUpdateAdminPlanShareFreeze(planId)

  if (!plan) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Manage Shares</DialogTitle>
          <DialogDescription>
            Manage who has access to &ldquo;{plan.name}&rdquo; and who can
            freeze it
          </DialogDescription>
        </DialogHeader>

        <PlanAccessManager
          shares={sharesData}
          isLoadingShares={isLoadingShares}
          canManage
          inviteLabel="Add"
          isInviting={createShareMutation.isPending}
          onInvite={(user, canFreeze, onDone) =>
            createShareMutation.mutate(
              { userId: String(user.id), canFreeze },
              { onSuccess: onDone }
            )
          }
          onToggleFreeze={(shareId, canFreeze) =>
            freezeMutation.mutate({ shareId, canFreeze })
          }
          togglingShareId={
            freezeMutation.isPending
              ? freezeMutation.variables?.shareId
              : undefined
          }
          onRemove={(shareId, onDone) =>
            deleteShareMutation.mutate({ shareId }, { onSuccess: onDone })
          }
          isRemoving={deleteShareMutation.isPending}
        />
      </DialogContent>
    </Dialog>
  )
}
